import { formatDateTimeET } from '../../utils/datetime.ts';
import type { NQMMRankingRow } from '../../services/api';

export interface NQMMOrderHandoff {
  symbol: string;
  action: 'BUY' | 'SELL';
  quantity: number;
  openInModal: true;
  secType: 'STK';
  orderType: 'MIDPRICE';
  price?: number;
}

export interface ManualNQMMPositionCorrection {
  symbol: string;
  quantity: number;
  current_price?: number;
}

export const formatNQMMPercent = (value?: number | null, _fraction = false) => {
  if (value === undefined || value === null || !Number.isFinite(Number(value))) return '—';
  return `${(Number(value) * 100).toFixed(1)}%`;
};

export const formatNQMMDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return formatDateTimeET(value);
  } catch {
    return value;
  }
};

export const formatNQMMDateOnly = (value?: string | null) => {
  if (!value) return '—';
  const match = String(value).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${match[1]}-${match[2]}-${match[3]}T12:00:00Z`));
};

export const formatNQMMTimeOnly = (value?: string | null) =>
  value ? `${String(value).slice(0, 5)} ET` : '—';

export const formatNQMMPrice = (value?: number | null) =>
  value === undefined || value === null || !Number.isFinite(Number(value))
    ? '—'
    : `$${Number(value).toFixed(2)}`;

export const formatNQMMQuantityChange = (value?: number | null) => {
  if (value === undefined || value === null || !Number.isFinite(Number(value))) return '—';
  const quantity = Number(value);
  return quantity > 0 ? `+${quantity}` : `${quantity}`;
};

export const buildNQMMOrderHandoff = (
  row: NQMMRankingRow,
  action: 'BUY' | 'SELL',
  quantity: number,
): NQMMOrderHandoff => {
  const quote = row.price_quote as Record<string, unknown> | undefined;
  const bid = Number(quote?.bid);
  const ask = Number(quote?.ask);
  const currentPrice = Number(row.current_price);
  const midpoint = Number.isFinite(bid) && bid > 0 && Number.isFinite(ask) && ask >= bid
    ? (bid + ask) / 2
    : currentPrice;

  return {
    symbol: row.symbol,
    action,
    quantity,
    openInModal: true,
    secType: 'STK',
    orderType: 'MIDPRICE',
    price: Number.isFinite(midpoint) && midpoint > 0 ? midpoint : undefined,
  };
};

export const buildManualNQMMPositionCorrection = (
  symbolInput: string,
  quantityInput: string,
  priceInput: string,
): ManualNQMMPositionCorrection | null => {
  const symbol = symbolInput.trim().toUpperCase();
  const quantity = Number.parseFloat(quantityInput);
  if (!symbol || Number.isNaN(quantity) || quantity === 0) return null;

  const parsedPrice = priceInput.trim() ? Number.parseFloat(priceInput) : undefined;
  return {
    symbol,
    quantity,
    current_price: parsedPrice && !Number.isNaN(parsedPrice) ? parsedPrice : undefined,
  };
};
